import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import ButtonLabel from '../../components/Button/ButtonLabel';
import FormFieldTokenNumber from '../../components/FormField/FormFieldTokenNumber';
import { CODE_INCORRECT_TOKEN } from '../../data/Constants';
import { useControllerFlowStepsContext } from '../../hooks/ControllerFlowStepsContext';
import { sendSocialData } from '../../services/socialNetWorkController';
import {
  validateCodeFromCellphone,
  validateCodeFromEmail
} from '../../services/tokenValidation';
import { mergeAccount, updateTelephoneComplement } from '../../services/user';
import { ErrorMessages } from '../../data/ErrorMessages';
import { SentryContextsEnum } from '../../utils/Sentry';
import { AttemptsTypeEnum } from '../../enums/AttemptsTypeEnum';
import { SendCodeToEmailEventsEnum } from '../../enums/SendCodeToEmailEventsEnum';
import { useAuthContext } from '../../hooks/AuthContext';
import StepHeader from '../../components/Wizard/StepHeader';
import TimerButton from '../../components/Button/TimerButton';
import RedirectSignIn from '../SignUpNativo/RedirectSignIn';

export interface CheckTokenProps {
  allowSkipStep?: boolean;
  goBackView?: React.Dispatch<React.SetStateAction<boolean>>;
  callCreateToken(e: any): any;
  previousPath?: number | null;
  nextPath?: number | null;
  handleChangeStep(e: number): void;
  context: any;
  isEmail?: boolean;
  updatePhone?: boolean;
}

export default function CheckToken({
  goBackView,
  callCreateToken,
  nextPath,
  handleChangeStep,
  context,
  isEmail,
  updatePhone
}: CheckTokenProps) {
  const {
    socialData,
    setSocialData,
    socialWithoutEmail,
    setSocialWithoutEmail,
    setControllerViews,
    tokenSocial,
    notCreateAccount,
    providerSocial,
    showErrorMessageServer
  } = useControllerFlowStepsContext();
  const { saveUserTokens } = useAuthContext();
  const { getValues, handleSubmit, submitForm, originDomainForm, setValue } =
    useContext<any>(context);
  const [fieldValidate, setValueFieldValidate] = useState({
    value: '',
    isValid: false
  });
  const [tokenStatus, setTokenStatus] = useState<'none' | 'error' | 'success'>(
    'none'
  );
  const [resendingCode, setResendingCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);
  const url = `/${originDomainForm}/verifica-token`;

  const navigate = useNavigate();

  useEffect(() => {
    navigate(url, { replace: true });
  }, [navigate]);

  // Auto-validar quando o código estiver completo
  useEffect(() => {
    if (fieldValidate.isValid && !tokenValidated && !loading) {
      validateToken();
    }
  }, [fieldValidate.isValid]);

  function previousStep() {
    if (goBackView) goBackView(true);
  }

  async function validateToken() {
    let valueKey;
    if (isEmail) {
      valueKey = socialData.email || getValues('email');
    } else {
      valueKey = getValues('cellPhoneNumber');
      valueKey = valueKey?.replace(/\D/g, '');
    }

    const code = fieldValidate.value;

    if (valueKey && code) {
      setLoading(true);
      setTokenStatus('none');

      try {
        const emailErrorMessage = ErrorMessages.CODE_EMAIL_VALIDATE_ACCOUNT;
        if (providerSocial)
          emailErrorMessage.context = SentryContextsEnum.SOCIAL_SIGNUP;

        if (isEmail)
          await validateCodeFromEmail(
            valueKey,
            code,
            SendCodeToEmailEventsEnum.CONFIRM_EMAIL,
            emailErrorMessage
          );
        else
          await validateCodeFromCellphone(
            valueKey,
            code,
            ErrorMessages.CODE_VALIDATE_PHONE
          );

        setValue('emailCode', code);
        setTokenStatus('success');
        setTokenValidated(true);
        setLoading(false);
      } catch (error: any) {
        console.log('error validate token');
        if (error?.code === CODE_INCORRECT_TOKEN) {
          setTokenStatus('error');
        } else {
          showErrorMessageServer();
        }
        setTokenValidated(false);
        setResendingCode(false);
        setLoading(false);
      }
    }
  }

  async function proceedToNextStep() {
    if (!tokenValidated) return;

    setLoading(true);

    try {
      if (updatePhone) {
        const valueKey = getValues('cellPhoneNumber')?.replace(/\D/g, '');
        await updateTelephoneComplement(
          socialData.email || getValues('email'),
          valueKey
        );
        nextStep();
      } else {
        if (notCreateAccount && !socialWithoutEmail) {
          nextStep();
        } else if (socialWithoutEmail) {
          await sendSocialToken();
        } else {
          await createAccount();
        }
      }
    } catch (error) {
      console.log('error on proceed to next step', error);
      showErrorMessageServer();
      setLoading(false);
    }
  }

  async function sendSocialToken() {
    const token = tokenSocial;

    try {
      const resp = await sendSocialData(token, providerSocial);
      setSocialData({ ...resp, userName: socialData?.userName });
      setValue('provider', providerSocial);
    } catch (error: any) {
      console.log(error);
      if (error?.statusCode === 404) await handleMergeAccount();
    }
  }

  async function handleMergeAccount() {
    try {
      const response = await mergeAccount({
        provider: providerSocial,
        providerToken: tokenSocial,
        email: getValues('email'),
        emailCode: fieldValidate.value
      });
      saveUserTokens(response);
      setControllerViews({ mergeAccount: true });
    } catch (error: any) {
      if (error?.statusCode === 404) {
        if (socialWithoutEmail) {
          setSocialWithoutEmail(false);
          nextStep();
        } else {
          await createAccount();
        }
      } else {
        setLoading(false);
        showErrorMessageServer();
      }
    }
  }

  async function resendCode() {
    setTokenStatus('none');
    setTokenValidated(false);
    setResendingCode(true);
    setLoading(true);
    await callCreateToken(true);
    setLoading(false);
  }

  async function createAccount() {
    if (providerSocial) {
      setValue('isSocial', true);
      setValue('provider', providerSocial);
      if (socialData.email) setValue('email', socialData.email);

      handleSubmit((data: any) => submitForm(data, nextStep, providerSocial))();
    } else {
      handleSubmit((data: any) => submitForm(data, nextStep))();
    }
  }

  function getPhoneNumber() {
    const phone = getValues('cellPhoneNumber');
    if (!phone) return '';
    try {
      const [areaCode, number] = phone.split(' ');
      const splitNumber = number.split('-');
      return `${areaCode} XXXXX-${splitNumber[1]}`;
    } catch {
      return '';
    }
  }

  function nextStep() {
    if (nextPath) {
      handleChangeStep(nextPath);
    }
  }

  return (
    <div className="card__section">
      <div className="card__top">
        <StepHeader
          title={`Vamos confirmar o ${isEmail ? 'e-mail' : 'telefone'}`}
          description={[
            `Enviamos um código com 6 números para o ${isEmail ? 'e-mail' : 'telefone'} "${isEmail ? getValues('email') : `+55 ${getPhoneNumber()}`}".`,
            `Confirme que você tem acesso ao ${isEmail ? 'e-mail' : 'telefone'} informando este código aqui.`
          ]}
        />

        <div>
          <FormFieldTokenNumber
            fieldValidate={fieldValidate}
            setState={setValueFieldValidate}
            resendingCode={resendingCode}
            status={tokenStatus}
          />

          <TimerButton
            name="validate-email"
            isLoading={loading}
            type={AttemptsTypeEnum.VALIDATE_EMAIL}
            handleResend={resendCode}
          />
        </div>
      </div>

      <div className="button-group">
        <Button
          id={`confirm-code-${isEmail ? 'email' : 'phone'}`}
          className="mt-8 mbi-0"
          full
          onClick={proceedToNextStep}
          loading={loading}
          disabled={!tokenValidated}
        >
          <ButtonLabel loading={loading}>continuar</ButtonLabel>
        </Button>

        <Button
          id={`goback-${isEmail ? 'email' : 'phone'}`}
          className="mt-16"
          customColors="btn-white"
          full
          onClick={previousStep}
        >
          <ButtonLabel>voltar</ButtonLabel>
        </Button>

        <RedirectSignIn context={context} />
      </div>
    </div>
  );
}
